import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, CircularProgress } from "@mui/material";
import { fetchRuntimeFormDetails } from "../services/FormService";
import { Form } from "react-formio";
import ContentLoader from "../animations/ContentLoader.json";
import Lottie from "lottie-react";
import { FORM_HIDDEN_KEYS } from "../constants/constants";
import Colors from "../theme/colors.json";
import ClientLogo from "../assets/svg/Techsophy-logo.svg";

const useStyles = makeStyles((theme) => ({
  mainConatiner: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "85%",
    pointerEvents: "none",
    fontSize: theme.fontSizes.SMALL,
  },
  loader: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center !important",
    alignItems: "center !important",
  },
  tableHead: {
    backgroundColor: Colors.FORM_HEADING_BG,
    color: theme.palette.secondary.main,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  tableCellHeader: {
    border: "1px solid #dddddd",
    textAlign: "left",
    padding: "0.7rem 0.7rem 0.7rem 1rem",
    fontWeight: 600,
  },
  tableCell: {
    border: "1px solid #dddddd",
    textAlign: "left",
    padding: "3px",
  },
  coveragesLimitIfAnyHeader: {
    // backgroundColor: Colors.INSUR_BG,
    border: "1px solid #dddddd",
  },
  coveragesLimitIfAnyContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    // padding: "0.5%",
  },
  coverageSection: {
    display: "flex",
    justifyContent: "flex-start",
    width: "40%",
    paddingLeft: "1rem",
  },
  limitIfAnySection: {
    display: "flex",
    justifyContent: "flex-start",
    width: "40%",
    paddingLeft: "1rem",
  },
  rfqHeadingText: {
    // backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    fontSize: "2vw",
    fontWeight: 700,
    display: "flex",
    justifyContent: "center",
  },
  formStylesOveride: {
    "& .choices__list--dropdown, & .choices__list": {
      height: "0px",
    },
    "& .remarks-section-formio": {
      display: "none",
    },
  },
  pdfPreviewContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
}));

const animationStyle = {
  height: "50vh",
};

const RFQPreview = (props) => {
  const { rfqContentRef } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const { proposalDataRedux } = useSelector((state: any) => ({
    proposalDataRedux: state.proposalData,
  }));

  const [proposalForm, setProposalForm] = useState<any>({});
  const [rfqData, setRfqData] = useState<any>({});

  // Get today's date
  const today = new Date();

  // Extract day, month, and year components
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = today.getFullYear();

  function convertToTitleFormat(text) {
    if (!text) {
      return "";
    }

    return text.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
      return str.toUpperCase();
    });
  }

  const getProposalForm = async (formId) => {
    const response: any = await fetchRuntimeFormDetails(formId);
    if (response.success) {
      const form = replaceDisableAddingRemovingRows(
        response?.data?.components || {}
      );
      setProposalForm(form);
    } else {
      setProposalForm({ display: "form", components: [] });
    }
  };

  function replaceDisableAddingRemovingRows(data) {
    if (Array.isArray(data)) {
      data.forEach(replaceDisableAddingRemovingRows);
    } else if (typeof data === "object" && data !== null) {
      Object.keys(data).forEach((key) => {
        if (key === FORM_HIDDEN_KEYS.disableAddingRemovingRows && !data[key]) {
          data[key] = true;
        }
        if (key === "label") {
          if (
            data[key] === FORM_HIDDEN_KEYS.relationShipManager ||
            data[key] === FORM_HIDDEN_KEYS.serviceManager
          ) {
            data["hidden"] = true;
          }
        } else if (typeof data[key] === "object" && data[key] !== null) {
          replaceDisableAddingRemovingRows(data[key]);
        }
      });
    }
    return data;
  }

  function toCamelCase(inputString) {
    // Check if inputString is empty, null, or undefined
    if (!inputString || typeof inputString !== "string") {
      return ""; // Or handle this case based on your requirements
    }

    const words = inputString.split(/\s+/);

    const camelCaseWords = words.map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    });

    return camelCaseWords.join("");
  }

  useEffect(() => {
    getProposalForm(proposalDataRedux?.formId);
    setRfqData(proposalDataRedux?.formData || {});
  }, []);

  return (
    <div className={classes.pdfPreviewContainer}>
      <div className={classes.mainConatiner}>
        {Object.keys(proposalForm)?.length === 0 ||
        Object.keys(rfqData)?.length === 0 ? (
          <Box className={classes.loader}>
            <Lottie
              animationData={ContentLoader}
              loop={true}
              style={animationStyle}
            />
            {/* <CircularProgress size={80} />
            ...Please Wait */}
          </Box>
        ) : null}
        {!(Object.keys(proposalForm)?.length === 0) &&
        !(Object.keys(rfqData)?.length === 0) ? (
          <div ref={rfqContentRef} className={classes.formStylesOveride}>
            <div className={classes.rfqHeadingText}>Request For Quote</div>
            <div className="techshophy-logo-container-preview">
              <img
                src={sessionStorage.getItem("logoURL") || ClientLogo}
                alt="client-logo"
              />
            </div>
            <Form
              form={proposalForm}
              submission={{ data: { ...rfqData } }}
              options={{
                readOnly: true,
                flatten: true,
                renderMode: "html",
              }}
            />
            <div>
              <table className={classes.table}>
                <thead className={classes.tableHead}></thead>
                <tbody>
                  <tr className={classes.tableHead}>
                    <th className={classes.tableCellHeader}>Terms</th>
                  </tr>
                  <tr className={classes.coveragesLimitIfAnyHeader}>
                    <th>
                      <div className={classes.coveragesLimitIfAnyContainer}>
                        <div className={classes.coverageSection}>
                          In-Built Covers / Add-on Covers / Conditions
                        </div>
                        <div className={classes.limitIfAnySection}>
                          Limit If any
                        </div>
                      </div>
                    </th>
                  </tr>
                  {proposalDataRedux?.formData?.underwriterDetails?.underwriterData?.map(
                    (item, index) => {
                      return (
                        <tr>
                          <td className={classes.tableCell}>
                            <div
                              className={classes.coveragesLimitIfAnyContainer}
                            >
                              <div className={classes.coverageSection}>
                                {item?.coverage}
                              </div>
                              <div className={classes.limitIfAnySection}>
                                {item?.proposedTermsLimitIfAny}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RFQPreview;
